import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {ConfirmDialog} from "primereact/confirmdialog";
import Icon from "../../../system/components/icon";
import {setMessage} from "../../../system/app-slice";
import {projectMeetingsApi} from "../../../models/project/project-meetings-api";
import {errorText, faNum} from "./meetings-labels";
import {DecisionsView, MeetingsView, OverviewView} from "./meetings-views";
import MeetingDrawer from "./meeting-drawer";
import DecisionDrawer from "./decision-drawer";
import MeetingFormDialog from "./meeting-form-dialog";
import OutputDialog from "./output-dialog";
import {DECISIONS_CHANGED} from "./task-decision-tag";
import "../dashboard/project-dashboard.css";
import "./project-meetings.css";

// «جلسات» — the project meetings tab (boss's task «طراحی تب جلسات و مودال های ان», spec «منطق تب جلسات زوفینو»;
// docs/tasks/26-project-meetings.md). Meeting → decision → task → result → goal / milestone, with what needs action
// first. Everything is stored on the server; tasks are the project's real tasks.

export const MEETINGS_CHANGED = "project-meetings:changed";

const VIEWS = [
    {id: "overview", label: "نیازمند اقدام", icon: "priority_high"},
    {id: "meetings", label: "جلسات", icon: "event"},
    {id: "decisions", label: "تصمیم‌ها و پیگیری‌ها", icon: "flag"},
];

export default function ProjectMeetings({projectData}) {
    const dispatch = useDispatch();
    const projectKey = projectData?.documentId;
    const [tab, setTab] = useState(null);
    const [failed, setFailed] = useState(false);
    const [view, setView] = useState("overview");
    const [meetingFilter, setMeetingFilter] = useState("all");
    const [decisionFilter, setDecisionFilter] = useState("all");
    const [meetingKey, setMeetingKey] = useState(null);
    const [decisionKey, setDecisionKey] = useState(null);
    const [focusTask, setFocusTask] = useState(null);
    const [form, setForm] = useState(null); // {meeting} for edit, {} for create
    const [outputFor, setOutputFor] = useState(null);

    const load = useCallback(async () => {
        if (!projectKey) return;
        try {
            setTab(await projectMeetingsApi.get(projectKey));
            setFailed(false);
        } catch (error) {
            setFailed(true);
        }
    }, [projectKey]);

    useEffect(() => {
        setTab(null);
        load();
    }, [load]);
    // a meeting made from the project header («افزودن ← جلسه») shows here at once
    useEffect(() => {
        const refresh = (event) => {
            if (event.detail?.project && event.detail.project !== projectKey) return;
            load();
            setView("meetings");
            setMeetingFilter("all");
        };
        window.addEventListener(MEETINGS_CHANGED, refresh);
        return () => window.removeEventListener(MEETINGS_CHANGED, refresh);
    }, [load, projectKey]);

    const toast = (content, severity = "success") => dispatch(setMessage({severity, content}));
    // every change goes to the server, then the tab is read again so all views show the same data
    // (dialogs and the decision panel show their own error, so they ask for no error toast)
    const act = async (work, message, quiet = false) => {
        try {
            const result = await work();
            await load();
            // task cards elsewhere show their decision
            window.dispatchEvent(new Event(DECISIONS_CHANGED));
            if (message) toast(message);
            return result;
        } catch (error) {
            if (!quiet) toast(errorText(error), "error");
            throw error;
        }
    };

    const meeting = useMemo(() => tab?.meetings.find((item) => item.key === meetingKey) || null, [tab, meetingKey]);
    const decision = useMemo(() => tab?.decisions.find((item) => item.key === decisionKey) || null, [tab, decisionKey]);

    const open = {
        view: (next, filter) => {
            setView(next);
            if (next === "meetings") setMeetingFilter(filter || "all");
            if (next === "decisions") setDecisionFilter(filter || "all");
        },
        meeting: (key) => {
            setDecisionKey(null);
            setMeetingKey(key);
        },
        decision: (key, taskKey = null) => {
            setMeetingKey(null);
            setFocusTask(taskKey);
            setDecisionKey(key);
        },
        task: (taskKey) => {
            const owner = tab?.decisions.find((item) => item.taskKeys.includes(taskKey));
            if (owner) open.decision(owner.key, taskKey);
        },
        linkDecision: (meetingRef, decisionRef) => act(() => projectMeetingsApi.linkDecision(projectKey, meetingRef, decisionRef), "همان تصمیم به جلسه پیوند شد؛ کار تکراری ساخته نشد."),
    };

    const meetingActions = {
        patchMeeting: (key, patch) => act(() => projectMeetingsApi.updateMeeting(projectKey, key, patch),
            patch.status ? "وضعیت جلسه ذخیره شد." : patch.notes !== undefined ? "صورت‌جلسه ذخیره شد." : patch.addTopic ? "موضوع به دستور جلسه اضافه شد." : null),
        linkDecision: open.linkDecision,
        archiveMeeting: (key) => act(async () => {
            await projectMeetingsApi.archiveMeeting(projectKey, key);
            setMeetingKey(null);
        }, "جلسه حذف شد."),
        editMeeting: () => setForm({meeting}),
        recordOutput: () => setOutputFor(meeting),
        openDecision: open.decision,
    };
    const decisionActions = {
        updateDecision: (key, patch) => act(() => projectMeetingsApi.updateDecision(projectKey, key, patch), "تغییرات تصمیم ذخیره شد.", true),
        addNote: (key, text) => act(() => projectMeetingsApi.addNote(projectKey, key, text), "پیگیری در سابقه ثبت شد.", true),
        addTask: (key, payload) => act(async () => {
            const result = await projectMeetingsApi.addTask(projectKey, key, payload);
            if (result?.alreadyLinked) toast("این کار از قبل به همین تصمیم وصل است.", "info");
            return result;
        }, payload.mode === "existing" ? "کار موجود به این تصمیم پیوند خورد؛ کار تکراری ساخته نشد." : "کار به جریان کارهای پروژه اضافه شد.", true),
        updateTask: (taskKey, patch) => act(() => projectMeetingsApi.updateTask(projectKey, taskKey, patch), "کار پروژه به‌روز شد.", true),
        openMeeting: open.meeting,
        openDecision: (key) => open.decision(key),
    };

    const saveMeeting = async (values) => {
        if (form?.meeting) {
            await act(() => projectMeetingsApi.updateMeeting(projectKey, form.meeting.key, values), "تغییرات جلسه ذخیره شد؛ حضور اعضای باقی‌مانده حفظ شد.", true);
        } else {
            const created = await act(() => projectMeetingsApi.createMeeting(projectKey, values), "جلسه‌ی جدید ساخته شد و دعوت‌شدگان مطلع شدند.", true);
            setView("meetings");
            setMeetingFilter("all");
            if (created?.key) setMeetingKey(created.key);
        }
        setForm(null);
    };
    const saveOutput = async (output) => {
        const created = await act(() => projectMeetingsApi.recordOutput(projectKey, outputFor.key, output),
            output.taskMode === "existing" ? "تصمیم ثبت شد و به کار موجود پیوند خورد." : "تصمیم ثبت شد و به پیگیری پروژه وصل شد.", true);
        setOutputFor(null);
        setView("decisions");
        setDecisionFilter("all");
        if (created?.key) open.decision(created.key);
    };

    if (failed) {
        return (
            <div className="pd-root"><div className="pd-container">
                <div className="pd-card pd-card-pad flex align-items-center gap-3">
                    <Icon icon="mi mi:cloud_off" className="tone-red"/>
                    <span className="text-xs flex-1">جلسات پروژه بارگذاری نشد.</span>
                    <button type="button" className="pd-btn small" onClick={load}>تلاش دوباره</button>
                </div>
            </div></div>
        );
    }
    if (!tab) {
        return <div className="pd-root"><div className="pd-container"><div className="pd-card pd-card-pad text-xs text-500 text-center">در حال بارگذاری جلسات…</div></div></div>;
    }

    const blocked = tab.decisions.filter((item) => item.status === "blocked").length;
    return (
        <div className="pd-root" dir="rtl">
            <div className="pd-container">
                <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                    <div>
                        <h1 className="pd-title text-lg">جلسات پروژه</h1>
                        <p className="pd-sub">از بحث تا تصمیم، از تصمیم تا نتیجه — قدم بعدی را گم نکنید.</p>
                    </div>
                    {tab.permissions.canCreate && (
                        <button type="button" className="pd-btn primary" onClick={() => setForm({})}>
                            <Icon icon="mi mi:event" className="text-lg"/>ایجاد جلسه
                        </button>
                    )}
                </div>

                <div className="mt-view-tabs" role="tablist" aria-label="نماهای جلسات پروژه">
                    {VIEWS.map((item) => (
                        <button key={item.id} type="button" role="tab" aria-selected={view === item.id} className={view === item.id ? "is-active" : ""} onClick={() => open.view(item.id)}>
                            <Icon icon={`mi mi:${item.icon}`} className="text-base"/>{item.label}
                            {item.id === "overview" && blocked > 0 && <span className="mt-badge">{faNum(blocked)}</span>}
                        </button>
                    ))}
                    <span className="flex-1"/>
                    <span className="pd-tiny text-500 mt-view-hint"><Icon icon="mi mi:route" className="text-xs"/> جلسه ← تصمیم ← کار ← نتیجه</span>
                </div>

                {view === "overview" && <OverviewView tab={tab} open={open}/>}
                {view === "meetings" && <MeetingsView tab={tab} open={open} filter={meetingFilter} setFilter={setMeetingFilter}/>}
                {view === "decisions" && <DecisionsView tab={tab} open={open} filter={decisionFilter} setFilter={setDecisionFilter}/>}
            </div>

            <MeetingDrawer meeting={meeting} tab={tab} visible={Boolean(meeting)} onHide={() => setMeetingKey(null)} actions={meetingActions}/>
            <DecisionDrawer decision={decision} tab={tab} visible={Boolean(decision)} focusTask={focusTask} onHide={() => {setDecisionKey(null); setFocusTask(null);}} actions={decisionActions}/>
            <MeetingFormDialog visible={Boolean(form)} onHide={() => setForm(null)} meeting={form?.meeting} people={tab.people.filter((item) => tab.members.includes(item.key))} knownPeople={tab.people} onSave={saveMeeting}/>
            <OutputDialog visible={Boolean(outputFor)} onHide={() => setOutputFor(null)} meeting={outputFor} tab={tab} onSubmit={saveOutput}/>
            <ConfirmDialog group="project-meetings" className="rtl"/>
        </div>
    );
}
